import { SignInPage, SignInProviderConfig } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import type { IdentityProviders } from '@backstage/core-components';
import { oidcAuthApiRef } from './oidcAuthApi';
import { Heading, Kopfzeile, Seitenkopf, SkipLink } from '@internal/kern-ux';

/** Sign-in via Keycloak. Backstage renders the tile; its colours come from KERN. Texts are German. */
const keycloakProvider: SignInProviderConfig = {
  id: 'keycloak',
  title: 'Anmeldung mit Keycloak',
  message:
    'Melden Sie sich mit Ihrer dienstlichen Kennung an. Sie werden dafür kurz zum Anmeldedienst weitergeleitet.',
  apiRef: oidcAuthApiRef,
};

/**
 * Sign-in page in the KERN frame.
 *
 * The frame — identification banner, application header, skip link, heading — is KERN. The
 * sign-in tiles deliberately still come from Backstage's `SignInPage`: session restoration,
 * popup handling and the identity objects depend on it, and Backstage does not export them
 * publicly. The tiles pick up the KERN colours through the theme.
 *
 * Which providers are offered is controlled by `app.signInProviders` in the configuration.
 */
export const KernSignInPage = (props: SignInPageProps) => {
  const config = useApi(configApiRef);
  const title = config.getOptionalString('app.title') ?? 'Backstage';
  const bannerLabel = config.getOptionalString('app.kern.banner.label');
  const showFlag = config.getOptionalBoolean('app.kern.banner.flag') ?? false;
  const operator = config.getOptionalString('app.kern.operator');
  const logoConfig = config.getOptionalConfig('app.kern.logo');
  const logo = logoConfig && {
    src: logoConfig.getString('src'),
    alt: logoConfig.getString('alt'),
  };

  const configured = config.getOptionalStringArray('app.signInProviders') ?? [
    'guest',
  ];
  const providers: IdentityProviders = configured.map(id =>
    id === 'keycloak' ? keycloakProvider : (id as 'guest' | 'custom'),
  );

  return (
    <div className="vf-app">
      <SkipLink />
      {bannerLabel && <Kopfzeile label={bannerLabel} flag={showFlag} />}
      <Seitenkopf title={title} subtitle={operator} logo={logo} />
      {/*
        No <main> of our own: `SignInPage` already renders a main landmark, and two of them
        on one page would be an accessibility error. The skip link target therefore sits on
        a focusable container.
      */}
      <div id="inhalt" tabIndex={-1} className="vf-page vf-signin">
        <Heading type="large" headerElement="h1" title="Anmeldung" />
        <SignInPage {...props} providers={providers} title="" align="left" />
      </div>
    </div>
  );
};
